import { useEffect, useState } from 'react';
import {
  MdLanguage,
  MdAddCircleOutline,
  MdEdit,
  MdPause,
  MdPlayArrow,
  MdArchive,
} from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { FloxSpinner } from '@tailwindComponents/FloxSpinner';
import {
  Modal,
  ModalContainer,
  ModalContent,
  ModalHeader,
} from '@components/Modal';
import { Button } from '@components/Button';
import {
  NETWORK_NAMES_LIST,
  NETWORK_RATECARD_ARCHIVE,
  NETWORK_RATECARD_SUSPEND,
} from '../../constants';
import { useAuthHttp } from '../../hooks';

type NetworkSummary = {
  id: string;
  networkName: string;
  isActive: boolean;
  zoneCount: number;
};

export function NetworkRatesSection() {
  const [networks, setNetworks] = useState<NetworkSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [pendingActionId, setPendingActionId] = useState<string | null>(null);
  const [networkToArchive, setNetworkToArchive] =
    useState<NetworkSummary | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const authHttp = useAuthHttp();
  const navigate = useNavigate();

  const fetchNetworks = async () => {
    setIsLoading(true);
    try {
      const { data } = await authHttp.get(NETWORK_NAMES_LIST);
      if (data?.networks) {
        setNetworks(data.networks);
      }
    } catch (error) {
      console.error('Failed to fetch networks:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchNetworks();
  }, []);

  const handleToggleSuspension = async (network: NetworkSummary) => {
    setPendingActionId(network.id);
    setActionError(null);
    try {
      await authHttp.post(NETWORK_RATECARD_SUSPEND, {
        id: network.id,
        isActive: !network.isActive,
      });
      await fetchNetworks();
    } catch (error) {
      console.error('Failed to change network rate card suspension:', error);
      setActionError(
        `Could not ${
          network.isActive ? 'suspend' : 'reactivate'
        } ${network.networkName}. Please try again.`
      );
    } finally {
      setPendingActionId(null);
    }
  };

  const handleArchive = async () => {
    if (!networkToArchive) return;

    setPendingActionId(networkToArchive.id);
    setActionError(null);
    try {
      await authHttp.post(NETWORK_RATECARD_ARCHIVE, {
        id: networkToArchive.id,
      });
      setNetworkToArchive(null);
      await fetchNetworks();
    } catch (error) {
      console.error('Failed to archive network rate card:', error);
      setActionError(
        `Could not archive ${networkToArchive.networkName}. Please try again.`
      );
    } finally {
      setPendingActionId(null);
    }
  };

  return (
    <div className='flex flex-col gap-2'>
      <div className='flex items-center justify-between mb-2'>
        <p className='text-sm text-gray-500'>
          Preferred transport rates for haulage networks you are a member of
        </p>
        <button
          type='button'
          className='flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors'
          onClick={() => navigate('/network-ratecard/add')}
        >
          <MdAddCircleOutline className='w-4 h-4' />
          Add network rates
        </button>
      </div>

      {actionError && (
        <div className='px-4 py-2 border border-red-200 bg-red-50 text-red-700 text-sm rounded-lg'>
          {actionError}
        </div>
      )}

      {isLoading && (
        <div className='flex items-center justify-center py-8'>
          <FloxSpinner size='sm' />
        </div>
      )}

      {!isLoading && networks.length === 0 && (
        <div className='border border-dashed border-gray-300 rounded-lg p-8 text-center'>
          <MdLanguage className='w-10 h-10 text-gray-300 mx-auto mb-3' />
          <p className='text-gray-500 text-sm'>No network rates configured</p>
          <p className='text-gray-400 text-xs mt-1'>
            Add the haulage networks you are a member of and configure your
            preferred transport rates for each.
          </p>
        </div>
      )}

      {!isLoading && networks.length > 0 && (
        <div className='flex flex-col gap-2'>
          {networks.map((network) => (
            <div
              key={network.id}
              className='flex items-center justify-between px-4 py-3 border border-gray-200 rounded-lg bg-white hover:border-gray-300 transition-colors'
            >
              <div className='flex items-center gap-3'>
                <MdLanguage className='w-6 h-6 text-gray-600' />
                <div className='flex flex-col'>
                  <div className='flex items-center gap-2'>
                    <span className='font-medium text-gray-900'>
                      {network.networkName}
                    </span>
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${
                        network.isActive
                          ? 'bg-green-100 text-green-700'
                          : 'bg-amber-100 text-amber-700'
                      }`}
                    >
                      {network.isActive ? 'Active' : 'Suspended'}
                    </span>
                  </div>
                  <span className='text-sm text-gray-500'>
                    {network.zoneCount} zone{network.zoneCount !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>
              <div className='flex items-center gap-1'>
                <button
                  type='button'
                  className='flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors'
                  onClick={() => navigate(`/network-ratecard/${network.id}/edit`)}
                >
                  <MdEdit className='w-4 h-4' />
                  Edit
                </button>
                <button
                  type='button'
                  disabled={pendingActionId === network.id}
                  className='flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-50 rounded-lg transition-colors disabled:opacity-50'
                  onClick={() => handleToggleSuspension(network)}
                >
                  {network.isActive ? (
                    <MdPause className='w-4 h-4' />
                  ) : (
                    <MdPlayArrow className='w-4 h-4' />
                  )}
                  {network.isActive ? 'Suspend' : 'Reactivate'}
                </button>
                <button
                  type='button'
                  disabled={pendingActionId === network.id}
                  className='flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50'
                  onClick={() => {
                    setActionError(null);
                    setNetworkToArchive(network);
                  }}
                >
                  <MdArchive className='w-4 h-4' />
                  Archive
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        open={networkToArchive !== null}
        onOpenChange={(open) => {
          if (!open) setNetworkToArchive(null);
        }}
      >
        <ModalContainer>
          <ModalHeader title='Archive network rate card' />
          <ModalContent>
            <div className='flex flex-col gap-2 py-2'>
              <p className='text-gray-900 text-sm'>
                Archiving {networkToArchive?.networkName} removes it from this
                list and stops it being used for shipment quotes and agent
                bookings.
              </p>
              <p className='text-gray-500 text-sm'>
                This cannot be undone. Suspend the rate card instead if you may
                want to use it again later.
              </p>
            </div>
            <div className='flex justify-end gap-2 mt-4'>
              <Button
                variant='secondary'
                onClick={() => setNetworkToArchive(null)}
              >
                Cancel
              </Button>
              <Button
                action='danger'
                isLoading={pendingActionId === networkToArchive?.id}
                loadingText='Archiving'
                disabled={pendingActionId === networkToArchive?.id}
                onClick={handleArchive}
              >
                Archive
              </Button>
            </div>
          </ModalContent>
        </ModalContainer>
      </Modal>
    </div>
  );
}
